import * as THREE from 'three';
import type { ViewportSnapshot } from '../core/ViewportService';

export type QualityTier = 'low' | 'medium' | 'high';
export interface FrameInfo { delta: number; elapsed: number; }
export interface RenderEngineEvents { onFrame: (frame: FrameInfo) => void; onHidden: () => void; onVisible: () => void; onContextLost: () => void; onContextRestored: () => void; }

export class RenderEngine {
  public readonly scene = new THREE.Scene();
  public readonly camera = new THREE.PerspectiveCamera(56, 1, 0.1, 720);
  public readonly renderer: THREE.WebGLRenderer;
  public readonly quality: QualityTier;
  public readonly world = new THREE.Group();
  private frame = 0;
  private elapsed = 0;
  private previousTime = 0;
  private enabled = true;
  private disposed = false;

  public constructor(private readonly host: HTMLElement, private readonly events: RenderEngineEvents) {
    this.quality = detectQuality();
    this.renderer = new THREE.WebGLRenderer({ antialias: this.quality !== 'low', alpha: false, powerPreference: 'high-performance', preserveDrawingBuffer: true });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.setClearColor(0x07141a, 1);
    this.renderer.shadowMap.enabled = false;
    this.renderer.domElement.setAttribute('aria-label', 'Astra Loom three-dimensional flight view');
    this.host.append(this.renderer.domElement);
    this.scene.background = new THREE.Color(0x07141a);
    this.scene.fog = new THREE.FogExp2(0x07141a, 0.0125);
    this.camera.position.set(0, 1.15, 9.5);
    this.camera.lookAt(0, 0, -20);
    this.scene.add(this.world);
    this.addLighting();
    document.addEventListener('visibilitychange', this.onVisibility);
    this.renderer.domElement.addEventListener('webglcontextlost', this.onContextLost, false);
    this.renderer.domElement.addEventListener('webglcontextrestored', this.onContextRestored, false);
  }

  public resize(viewport: ViewportSnapshot): void {
    const tierCap = this.quality === 'low' ? 1 : this.quality === 'medium' ? 1.5 : 2;
    this.renderer.setPixelRatio(Math.min(viewport.pixelRatio, tierCap));
    this.renderer.setSize(viewport.width, viewport.height, false);
    this.camera.aspect = viewport.width / viewport.height;
    this.camera.fov = viewport.compact ? 63 : 56;
    this.camera.updateProjectionMatrix();
  }

  public start(): void { if (this.frame || this.disposed) return; this.previousTime = performance.now(); this.frame = requestAnimationFrame(this.loop); }
  public setEnabled(enabled: boolean): void { this.enabled = enabled; this.previousTime = performance.now(); }

  public renderStill(): void { if (!this.disposed) this.renderer.render(this.scene, this.camera); }

  public dispose(): void {
    if (this.disposed) return; this.disposed = true; cancelAnimationFrame(this.frame); this.frame = 0;
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.renderer.domElement.removeEventListener('webglcontextlost', this.onContextLost);
    this.renderer.domElement.removeEventListener('webglcontextrestored', this.onContextRestored);
    this.scene.traverse((object) => {
      if (!(object instanceof THREE.Mesh || object instanceof THREE.Line || object instanceof THREE.Points || object instanceof THREE.InstancedMesh)) return;
      object.geometry.dispose(); const materials = Array.isArray(object.material) ? object.material : [object.material]; for (const material of materials) material.dispose();
    });
    this.renderer.renderLists.dispose(); this.renderer.dispose(); this.renderer.forceContextLoss(); this.renderer.domElement.remove(); this.scene.clear();
  }

  private addLighting(): void {
    this.scene.add(new THREE.HemisphereLight(0x9ebbc1, 0x07141a, 1.15));
    const key = new THREE.DirectionalLight(0xf1eadb, 2.2); key.position.set(-4, 7, 5); this.scene.add(key);
    const copper = new THREE.PointLight(0xca8b67, 14, 34, 2); copper.position.set(4, -2, -5); this.scene.add(copper);
  }

  private readonly loop = (time: number): void => {
    if (this.disposed) return;
    const rawDelta = (time - this.previousTime) / 1000; this.previousTime = time;
    const delta = this.enabled ? Math.min(rawDelta, .05) : 0; this.elapsed += delta;
    if (this.enabled) { this.events.onFrame({ delta, elapsed: this.elapsed }); this.renderer.render(this.scene, this.camera); }
    this.frame = requestAnimationFrame(this.loop);
  };

  private readonly onVisibility = (): void => {
    if (document.hidden) { this.setEnabled(false); this.events.onHidden(); } else { this.events.onVisible(); } this.previousTime = performance.now();
  };
  private readonly onContextLost = (event: Event): void => { event.preventDefault(); this.setEnabled(false); this.events.onContextLost(); };
  private readonly onContextRestored = (): void => { this.events.onContextRestored(); };
}

export function supportsWebGL(): boolean {
  try { const canvas = document.createElement('canvas'); return Boolean(canvas.getContext('webgl2') || canvas.getContext('webgl')); } catch { return false; }
}

function detectQuality(): QualityTier {
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4;
  const cores = navigator.hardwareConcurrency || 4;
  const reducedData = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData ?? false;
  if (reducedData || memory <= 2 || cores <= 2) return 'low';
  if (memory <= 4 || cores <= 4 || window.innerWidth < 820) return 'medium';
  return 'high';
}
